import * as THREE from 'three';
import { flattenObject3DForInstancing } from './amhsKitSceneClone';
import { instancedAssetManager, type SubMeshPart } from './InstancedAssetManager';

export type AmhsKitTemplateKey =
  | 'CleanroomWorkcell'
  | 'Awp300'
  | 'IndustrialVerticalLifter3D'
  | 'Stocker3D'
  | 'CircleStocker'
  | 'JunctionRing'
  | 'OhtVehicle'
  | 'FoupDefault';

const templateParts = new Map<AmhsKitTemplateKey, SubMeshPart[]>();
const flatGroups = new Map<AmhsKitTemplateKey, THREE.Group>();
const listeners = new Set<() => void>();

let revision = 0;
let notifySuspended = false;

export function getAmhsKitTemplateRevision(): number {
  return revision;
}

/** Batch several template registrations — one React refresh at the end. */
export function setAmhsKitTemplateNotifySuspended(suspended: boolean): void {
  notifySuspended = suspended;
}

export function flushAmhsKitTemplateNotify(): void {
  if (notifySuspended) return;
  notify();
}

export function subscribeAmhsKitTemplates(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  if (notifySuspended) return;
  revision += 1;
  for (const listener of listeners) {
    listener();
  }
}

export function registerAmhsKitTemplateFromScene(
  key: AmhsKitTemplateKey,
  sceneRoot: THREE.Object3D,
  options?: { silent?: boolean }
): SubMeshPart[] {
  const flat = flattenObject3DForInstancing(sceneRoot);
  flatGroups.set(key, flat);

  const assetKey = `AmhsKitTemplate:${key}`;
  let parts = instancedAssetManager.getAssetParts(assetKey);
  if (!parts || parts.length === 0) {
    parts = instancedAssetManager.registerAsset(assetKey, flat);
  }
  templateParts.set(key, parts);
  if (!options?.silent) notify();
  return parts;
}

export function getAmhsKitTemplateFlatGroup(key: AmhsKitTemplateKey): THREE.Group | null {
  return flatGroups.get(key) ?? null;
}

export function getAmhsKitTemplateParts(
  key: AmhsKitTemplateKey,
  fallback: () => SubMeshPart[]
): SubMeshPart[] {
  const parts = templateParts.get(key);
  if (parts && parts.length > 0) return parts;
  return fallback();
}

export function isAmhsKitTemplateReady(key: AmhsKitTemplateKey): boolean {
  return (templateParts.get(key)?.length ?? 0) > 0;
}
